/**
 * Public Content Viewer Page
 * Displays public content (flipbooks, images, videos) without requiring authentication
 * Used for sharing content with 3rd party apps and generating shareable links
 */

import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "../services/supabase";
import { getPublicUrl } from "../services/storage.service";
import { incrementViewCount } from "../services/content.service";
import LoadingSpinner from "../components/common/LoadingSpinner";
import FlipbookViewer from "../components/content/FlipbookViewer";
import {
  DocumentIcon,
  PhotoIcon,
  VideoCameraIcon,
  ArrowDownTrayIcon,
  ShareIcon,
} from "@heroicons/react/24/outline";

// Storage bucket
const CONTENT_BUCKET = "content";

interface PublicContentItem {
  id: string;
  title: string;
  description?: string;
  content_type: "FLIPBOOK" | "IMAGE" | "VIDEO_LINK" | "FILE";
  tags: string[];
  thumbnail_url?: string;
  is_public: boolean;
}

interface FlipbookData {
  page_count: number;
  page_urls: string[];
  storage_path: string;
  processing_status: string;
}

interface ImageData {
  storage_path: string;
  alt_text?: string;
  width?: number;
  height?: number;
}

interface VideoData {
  url: string;
  platform?: string;
  video_id?: string;
}

interface FileData {
  storage_path: string;
  file_name: string;
  file_size?: number;
  mime_type?: string;
}

export default function ContentViewer() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const [showFlipbookViewer, setShowFlipbookViewer] = useState(false);
  const [copied, setCopied] = useState(false);

  // Check if we should auto-open flipbook
  const autoOpen = searchParams.get("view") === "flipbook";

  // Fetch the public content item
  const {
    data: content,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["public-content", id],
    queryFn: async () => {
      if (!id) {
        throw new Error("Content ID is required");
      }

      // Fetch the content item
      const { data: item, error: itemError } = await supabase
        .from("content_items")
        .select("*")
        .eq("id", id)
        .eq("is_public", true)
        .single();

      if (itemError || !item) {
        throw new Error("Content not found or not public");
      }

      return item as PublicContentItem;
    },
    enabled: !!id,
  });

  // Fetch type-specific details
  const { data: details } = useQuery({
    queryKey: ["public-content-details", id, content?.content_type],
    queryFn: async () => {
      if (!id || !content) {
        return null;
      }

      switch (content.content_type) {
        case "FLIPBOOK": {
          const { data } = await supabase
            .from("content_flipbooks")
            .select("*")
            .eq("content_item_id", id)
            .single();
          return data as FlipbookData | null;
        }
        case "IMAGE": {
          const { data } = await supabase
            .from("content_images")
            .select("*")
            .eq("content_item_id", id)
            .single();
          return data as ImageData | null;
        }
        case "VIDEO_LINK": {
          const { data } = await supabase
            .from("content_videos")
            .select("*")
            .eq("content_item_id", id)
            .single();
          return data as VideoData | null;
        }
        case "FILE": {
          const { data } = await supabase
            .from("content_files")
            .select("*")
            .eq("content_item_id", id)
            .single();
          return data as FileData | null;
        }
        default:
          return null;
      }
    },
    enabled: !!id && !!content,
  });

  // Track view on mount
  useEffect(() => {
    if (id && content) {
      incrementViewCount(id);
    }
  }, [id, content]);

  // Auto-open flipbook if requested
  useEffect(() => {
    if (autoOpen && content?.content_type === "FLIPBOOK" && details) {
      setShowFlipbookViewer(true);
    }
  }, [autoOpen, content, details]);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement("textarea");
      textarea.value = url;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const getVideoEmbedUrl = (video: VideoData) => {
    if (video.platform === "YOUTUBE" && video.video_id) {
      return `https://www.youtube.com/embed/${video.video_id}`;
    }
    if (video.platform === "VIMEO" && video.video_id) {
      return `https://player.vimeo.com/video/${video.video_id}`;
    }
    if (video.platform === "WISTIA" && video.video_id) {
      return `https://fast.wistia.net/embed/iframe/${video.video_id}`;
    }
    return video.url;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !content) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <DocumentIcon className="h-16 w-16 mx-auto text-gray-400 mb-4" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">
            Content Not Found
          </h1>
          <p className="text-gray-600">
            This content doesn't exist or is not publicly accessible.
          </p>
        </div>
      </div>
    );
  }

  // Render flipbook viewer
  if (showFlipbookViewer && content.content_type === "FLIPBOOK") {
    return (
      <FlipbookViewer
        contentItemId={id!}
        title={content.title}
        onClose={() => setShowFlipbookViewer(false)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {content.content_type === "FLIPBOOK" && (
              <DocumentIcon className="h-8 w-8 text-blue-600" />
            )}
            {content.content_type === "IMAGE" && (
              <PhotoIcon className="h-8 w-8 text-green-600" />
            )}
            {content.content_type === "VIDEO_LINK" && (
              <VideoCameraIcon className="h-8 w-8 text-red-600" />
            )}
            {content.content_type === "FILE" && (
              <DocumentIcon className="h-8 w-8 text-gray-600" />
            )}
            <div>
              <h1 className="text-xl font-semibold text-gray-900">
                {content.title}
              </h1>
              {content.tags.length > 0 && (
                <div className="flex gap-1 mt-1">
                  {content.tags.slice(0, 3).map((tag) => (
                    <span
                      key={tag}
                      className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
          <button
            onClick={handleShare}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <ShareIcon className="h-4 w-4" />
            {copied ? "Copied!" : "Share"}
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 py-8">
        {content.description && (
          <p className="text-gray-600 mb-6">{content.description}</p>
        )}

        {/* Flipbook */}
        {content.content_type === "FLIPBOOK" && details && (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            {(details as FlipbookData).processing_status === "ready" ? (
              <>
                {/* Thumbnail/Preview */}
                <div
                  className="relative cursor-pointer group"
                  onClick={() => setShowFlipbookViewer(true)}
                >
                  <img
                    src={
                      content.thumbnail_url ||
                      (details as FlipbookData).page_urls?.[0]
                    }
                    alt={content.title}
                    className="w-full h-96 object-contain bg-gray-50"
                  />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <span className="text-white text-lg font-medium bg-blue-600 px-6 py-3 rounded-lg">
                      View Flipbook
                    </span>
                  </div>
                </div>
                <div className="p-4 border-t">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">
                      {(details as FlipbookData).page_count} pages
                    </span>
                    <button
                      onClick={() => setShowFlipbookViewer(true)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Open Flipbook
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="p-8 text-center">
                <LoadingSpinner size="md" />
                <p className="mt-4 text-gray-600">
                  This flipbook is still being processed. Please check back
                  shortly.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Image */}
        {content.content_type === "IMAGE" && details && (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <img
              src={getPublicUrl(
                "supabase",
                CONTENT_BUCKET,
                (details as ImageData).storage_path,
              )}
              alt={(details as ImageData).alt_text || content.title}
              className="w-full max-h-[80vh] object-contain bg-gray-50"
            />
            <div className="p-4 border-t flex items-center justify-between">
              <span className="text-sm text-gray-500">
                {(details as ImageData).width && (details as ImageData).height
                  ? `${(details as ImageData).width} x ${(details as ImageData).height}`
                  : "Image"}
              </span>
              <a
                href={getPublicUrl(
                  "supabase",
                  CONTENT_BUCKET,
                  (details as ImageData).storage_path,
                )}
                download
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                Download
              </a>
            </div>
          </div>
        )}

        {/* Video */}
        {content.content_type === "VIDEO_LINK" && details && (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="aspect-video">
              <iframe
                src={getVideoEmbedUrl(details as VideoData)}
                title={content.title}
                className="w-full h-full"
                allowFullScreen
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              />
            </div>
          </div>
        )}

        {/* File */}
        {content.content_type === "FILE" && details && (
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <DocumentIcon className="h-16 w-16 mx-auto text-gray-400 mb-4" />
            <h2 className="text-lg font-medium text-gray-900 mb-2">
              {(details as FileData).file_name}
            </h2>
            {(details as FileData).file_size && (
              <p className="text-sm text-gray-500 mb-4">
                {formatFileSize((details as FileData).file_size!)}
              </p>
            )}
            <a
              href={getPublicUrl(
                "supabase",
                CONTENT_BUCKET,
                (details as FileData).storage_path,
              )}
              download={(details as FileData).file_name}
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <ArrowDownTrayIcon className="h-5 w-5" />
              Download File
            </a>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="py-8 text-center text-sm text-gray-500">
        <p>Shared via USLS Relevate</p>
      </footer>
    </div>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) {
    return "0 Bytes";
  }
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}
